import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import BrandLogo from "../components/BrandLogo";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { trackEvent, trackPageView } from "../lib/analytics";
import { clearAccountantPartnership, readAccountantPartnership } from "../lib/accountantPartnership";
import { 
  Plus, 
  Building2, 
  FileText, 
  Clock, 
  CheckCircle2, 
  ArrowRight,
  LogOut,
  BarChart3,
  CreditCard
} from "lucide-react";

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [diagnostics, setDiagnostics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewCompany, setShowNewCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [newCompanyCnpj, setNewCompanyCnpj] = useState("");
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [partnership, setPartnership] = useState(() => readAccountantPartnership());

  useEffect(() => {
    fetchData();
    trackPageView("client_dashboard_view", { path: "/dashboard" });
  }, []);

  const fetchData = async () => {
    try {
      const [companiesRes, diagnosticsRes] = await Promise.all([
        axios.get(`${API}/companies`, { withCredentials: true }),
        axios.get(`${API}/diagnostics`, { withCredentials: true })
      ]);
      setCompanies(companiesRes.data.companies || []);
      setDiagnostics(diagnosticsRes.data.diagnostics || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    setCreatingCompany(true);
    
    try {
      const response = await axios.post(`${API}/companies`, {
        name: newCompanyName,
        cnpj: newCompanyCnpj || null
      }, { withCredentials: true });
      
      setCompanies([...companies, response.data]);
      setNewCompanyName("");
      setNewCompanyCnpj("");
      setShowNewCompany(false);
      trackEvent("company_create_success", {
        path: "/dashboard",
        metadata: { company_name: response.data?.name || newCompanyName },
      });
      toast.success("Empresa cadastrada com sucesso!");
    } catch (error) {
      console.error("Error creating company:", error);
      toast.error("Erro ao cadastrar empresa");
    } finally {
      setCreatingCompany(false);
    }
  };

  const handleStartDiagnostic = async (companyId) => {
    try {
      const response = await axios.post(`${API}/diagnostics`, {
        company_id: companyId,
        partnership_token: partnership?.token || null,
      }, { withCredentials: true });

      if (partnership?.token) {
        clearAccountantPartnership();
        setPartnership(null);
      }
      
      trackEvent("diagnostic_start_click", {
        path: "/dashboard",
        metadata: { company_id: companyId, diagnostic_id: response.data?.diagnostic_id },
      });
      toast.success("Diagnóstico iniciado!");
      navigate(`/diagnostic/${response.data.diagnostic_id}`);
    } catch (error) {
      console.error("Error starting diagnostic:", error);
      toast.error("Erro ao iniciar diagnóstico");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const formatPercent = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "0.0%";
    return `${numericValue.toFixed(1)}%`;
  };

  const getStatusBadge = (status) => {
    if (status === "submitted") {
      return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Concluído</Badge>;
    }
    if (status === "awaiting_payment") {
      return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Aguardando Pagamento</Badge>;
    }
    return <Badge variant="secondary">Em Andamento</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/dashboard" className="flex items-center">
            <BrandLogo size="md" className="max-w-[220px]" />
          </Link>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600 hidden sm:block">
              Olá, {user?.name?.split(" ")[0]}
            </span>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Welcome Section */}
        <div className="mb-10">
          <h1 className="text-3xl font-serif font-bold text-navy mb-2">
            Meus Diagnósticos
          </h1>
          <p className="text-slate-600">
            Gerencie suas empresas e diagnósticos empresariais.
          </p>
          {partnership?.token && (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
              Parceria de contador ativa.
              {Number(partnership.discount_percent || 0) > 0
                ? ` O próximo diagnóstico será iniciado com ${partnership.discount_percent}% de desconto.`
                : " O próximo diagnóstico ficará vinculado ao contador parceiro, sem desconto neste momento."}
            </div>
          )}
        </div>

        {/* Companies Section */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-serif font-semibold text-navy">Minhas Empresas</h2>
            <Dialog open={showNewCompany} onOpenChange={setShowNewCompany}>
              <DialogTrigger asChild>
                <Button data-testid="new-company-btn" className="bg-navy hover:bg-navy/90">
                  <Plus className="h-4 w-4 mr-2" />
                  Nova Empresa
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-serif">Cadastrar Nova Empresa</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateCompany} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="companyName">Nome da Empresa *</Label>
                    <Input
                      id="companyName"
                      value={newCompanyName}
                      onChange={(e) => setNewCompanyName(e.target.value)}
                      placeholder="Nome da empresa"
                      required
                      data-testid="company-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="companyCnpj">CNPJ (opcional)</Label>
                    <Input
                      id="companyCnpj"
                      value={newCompanyCnpj}
                      onChange={(e) => setNewCompanyCnpj(e.target.value)}
                      placeholder="00.000.000/0000-00"
                      data-testid="company-cnpj-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-navy hover:bg-navy/90"
                    disabled={creatingCompany}
                    data-testid="company-submit-btn"
                  >
                    {creatingCompany ? "Cadastrando..." : "Cadastrar Empresa"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {companies.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 mb-4">Você ainda não cadastrou nenhuma empresa.</p>
                <Button 
                  onClick={() => setShowNewCompany(true)}
                  className="bg-navy hover:bg-navy/90"
                  data-testid="empty-new-company-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Cadastrar Primeira Empresa
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {companies.map((company) => {
                const companyDiagnostics = diagnostics.filter(d => d.company_id === company.company_id);
                const hasDraft = companyDiagnostics.some(d => d.status === "draft");
                
                return (
                  <Card key={company.company_id} className="card-hover">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg font-serif">{company.name}</CardTitle>
                          {company.cnpj && (
                            <CardDescription className="font-mono text-xs">
                              {company.cnpj}
                            </CardDescription>
                          )}
                        </div>
                        <Building2 className="h-5 w-5 text-slate-400" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
                        <span className="flex items-center gap-1">
                          <FileText className="h-4 w-4" />
                          {companyDiagnostics.length} diagnóstico(s)
                        </span>
                      </div>
                      <Button 
                        onClick={() => handleStartDiagnostic(company.company_id)}
                        className="w-full bg-navy hover:bg-navy/90"
                        data-testid={`start-diagnostic-btn-${company.company_id}`}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        {hasDraft ? "Novo Diagnóstico" : "Iniciar Diagnóstico"}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </section>

        {/* Diagnostics Section */}
        {diagnostics.length > 0 && (
          <section>
            <h2 className="text-xl font-serif font-semibold text-navy mb-6">Diagnósticos Recentes</h2>
            <div className="space-y-4">
              {diagnostics.map((diagnostic) => (
                <Card key={diagnostic.diagnostic_id} className="card-hover">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-navy/10 rounded flex items-center justify-center">
                          {diagnostic.status === "submitted" ? (
                            <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                          ) : (
                            <Clock className="h-6 w-6 text-amber-600" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-medium text-navy">{diagnostic.company_name}</h3>
                          <p className="text-sm text-slate-600">
                            {diagnostic.status === "submitted" 
                              ? `Índice: ${formatPercent(diagnostic.overall_percent)}` 
                              : diagnostic.status === "awaiting_payment"
                              ? `${diagnostic.payment_summary?.status_label || "Finalize o pagamento para liberar o resultado"}`
                              : `${diagnostic.answers_count}/62 questões respondidas`
                            }
                          </p>
                          {diagnostic.journey_stage?.label && diagnostic.status !== "submitted" && (
                            <p className="text-xs text-slate-500 mt-1">{diagnostic.journey_stage.label}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(diagnostic.status)}
                        {diagnostic.status === "submitted" ? (
                          <Link to={`/results/${diagnostic.diagnostic_id}`}>
                            <Button variant="outline" size="sm" data-testid={`view-results-btn-${diagnostic.diagnostic_id}`}>
                              <BarChart3 className="h-4 w-4 mr-2" />
                              Ver Resultados
                            </Button>
                          </Link>
                        ) : diagnostic.status === "awaiting_payment" ? (
                          <Link to={`/payment/diagnostic/${diagnostic.diagnostic_id}`}>
                            <Button size="sm" className="bg-navy hover:bg-navy/90" data-testid={`open-payment-btn-${diagnostic.diagnostic_id}`}>
                              <CreditCard className="h-4 w-4 mr-2" />
                              Ir para Pagamento
                            </Button>
                          </Link>
                        ) : (
                          <Link to={`/diagnostic/${diagnostic.diagnostic_id}`}>
                            <Button size="sm" className="bg-navy hover:bg-navy/90" data-testid={`continue-diagnostic-btn-${diagnostic.diagnostic_id}`}>
                              Continuar
                              <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default ClientDashboard;
